import { c as _c } from "react/compiler-runtime";
import { FormLabel, FormControl, Stack, Divider, Box, Select, MenuItem, Tooltip } from '@mui/material';
import { AccountState, type Account } from '@repo/api-gw-sdk';
import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { FormTextField } from '@/components/form/formTextField';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getDocsUrl } from '@/docs/docsUrls';
import { mrapAliasFieldValidation } from './byovPolicyUtils';
import { MrapCopyablePolicy } from './mrapCopyablePolicy';
import { VaultOwnBucketCopyableAllAccountsPolicy } from './vaultOwnBucketCopyablePolicy';
import { VaultOwnBucketEditKeyPolicyButton } from './vaultOwnBucketEditKeyPolicyButton';
import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelWrapper } from '../panels/panelWrapper';
import { useBuckets } from '../restore/useBuckets';
import { SelectPlaceholder } from '../selectPlaceholder';
import { ReadMoreLink } from '../shared/readMoreLink';
export const SelectVaultBucketPanel = t0 => {
  const $ = _c(64);
  const {
    open,
    onClose,
    disabled,
    data,
    setData,
    vaultAccounts
  } = t0;
  const {
    byovEnabled: t1,
    EnableMrapForByov: t2
  } = useFeatureFlags();
  const byovEnabled = t1 === undefined ? false : t1;
  const enableMrapForByov = t2 === undefined ? false : t2;
  const enableMrap = byovEnabled && enableMrapForByov;
  let t3;
  if ($[0] !== data.vaultAttributes.aws?.mrapAlias) {
    t3 = data.vaultAttributes.aws?.mrapAlias?.trim() ?? "";
    $[0] = data.vaultAttributes.aws?.mrapAlias;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const mrapAlias = t3;
  let t4;
  if ($[2] !== data.vaultAccountId || $[3] !== vaultAccounts) {
    let t5;
    if ($[5] !== data.vaultAccountId) {
      t5 = account => account.id === data.vaultAccountId;
      $[5] = data.vaultAccountId;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = vaultAccounts.find(t5);
    $[2] = data.vaultAccountId;
    $[3] = vaultAccounts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const selectedVaultAccount = t4;
  let t5;
  let t6;
  if ($[7] !== onClose) {
    t5 = {
      title: "Select bucket",
      subtitle: "Select a bucket from your self-managed vault account. You\u2019re responsible for the vault\u2019s security, availability, and ensuring Eon has access to manage snapshots.",
      fullWidthSubtitle: false,
      onClose
    };
    t6 = {
      primaryButton: {
        text: "Done",
        "data-testid": "done-button",
        onClick: onClose
      },
      secondaryButton: {
        text: "Cancel",
        "data-testid": "cancel-button",
        onClick: onClose
      }
    };
    $[7] = onClose;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: "40px min(100px,6vw)",
      width: "100%",
      height: "auto"
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Divider sx={{
      borderStyle: "dashed"
    }} />;
    t9 = {
      gap: "40px"
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      gap: "24px"
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = data.vaultAccountId || "";
  let t12;
  if ($[14] !== data || $[15] !== setData) {
    t12 = vaultAccountId => {
      setData({
        ...data,
        vaultAccountId,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: undefined
        }
      });
    };
    $[14] = data;
    $[15] = setData;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== disabled || $[18] !== t11 || $[19] !== t12 || $[20] !== vaultAccounts) {
    t13 = <VaultAccountSelector disabled={disabled} vaultAccountId={t11} setAccountId={t12} vaultAccounts={vaultAccounts} />;
    $[17] = disabled;
    $[18] = t11;
    $[19] = t12;
    $[20] = vaultAccounts;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== selectedVaultAccount) {
    t14 = selectedVaultAccount || {} as Account;
    $[22] = selectedVaultAccount;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = data.region || "";
  const t16 = data.vaultAttributes.aws?.bucketName || "";
  let t17;
  if ($[24] !== data || $[25] !== setData) {
    t17 = bucketName => {
      setData({
        ...data,
        vaultAttributes: {
          ...data.vaultAttributes,
          aws: {
            ...data.vaultAttributes.aws,
            bucketName
          }
        }
      });
    };
    $[24] = data;
    $[25] = setData;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== disabled || $[28] !== t14 || $[29] !== t15 || $[30] !== t16 || $[31] !== t17) {
    t18 = <BucketSelector account={t14} region={t15} disabled={disabled} bucketName={t16} setBucketName={t17} />;
    $[27] = disabled;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t13 || $[34] !== t18) {
    t19 = <NumericBulletSection bulletNumber={1} title="Select vault account and bucket"><BulletItem leftContent={<Stack sx={t10}>{t13}{t18}</Stack>} /></NumericBulletSection>;
    $[33] = t13;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = data.vaultAttributes.aws?.bucketName || "";
  let t21;
  if ($[36] !== data.region || $[37] !== t20) {
    t21 = <VaultOwnBucketEditKeyPolicyButton region={data.region} bucketName={t20} />;
    $[36] = data.region;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = data.vaultAttributes.aws?.bucketName || "";
  let t23;
  if ($[39] !== t22) {
    t23 = <VaultOwnBucketCopyableAllAccountsPolicy bucketName={t22} />;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t21 || $[42] !== t23) {
    t24 = <NumericBulletSection bulletNumber={2} title="Add policy in AWS"><BulletItem title="Review and add this policy in your AWS account. Afterward, come back to this tab to finish the connection." leftContent={t21} rightContent={t23} /></NumericBulletSection>;
    $[41] = t21;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== data || $[45] !== enableMrap || $[46] !== setData) {
    t25 = enableMrap && <NumericBulletSection bulletNumber={3} title="Configure Multi-Region Access Point (optional)"><BulletItem title="If you need cross-region restore into isolated subnets, provide the alias of an S3 Multi-Region Access Point configured for this bucket." leftContent={<Stack sx={{
        gap: "8px"
      }}><FormTextField size="small" title="" placeholder="e.g. mfzwi23gnjvgw.mrap" validation={mrapAliasFieldValidation} value={data.vaultAttributes.aws?.mrapAlias ?? ""} onChange={e => {
          setData({
            ...data,
            vaultAttributes: {
              ...data.vaultAttributes,
              aws: {
                ...data.vaultAttributes.aws,
                mrapAlias: e.target.value
              }
            }
          });
        }} dataTestId="vault-mrap-alias-input" /><ReadMoreLink href={getDocsUrl("awsRestoreVpcEndpoints")} /></Stack>} /></NumericBulletSection>;
    $[44] = data;
    $[45] = enableMrap;
    $[46] = setData;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== enableMrap || $[49] !== mrapAlias || $[50] !== selectedVaultAccount) {
    t26 = enableMrap && mrapAlias && <NumericBulletSection bulletNumber={4} title="Add MRAP policy in AWS"><BulletItem title="Review and add this policy on your Multi-Region Access Point. This allows Eon restore nodes to read objects through the MRAP." leftContent={<MrapCopyablePolicy mrapAlias={mrapAlias} vaultAccount={selectedVaultAccount} />} /></NumericBulletSection>;
    $[48] = enableMrap;
    $[49] = mrapAlias;
    $[50] = selectedVaultAccount;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t19 || $[53] !== t24 || $[54] !== t25 || $[55] !== t26) {
    t27 = <Box sx={t7}><Stack divider={t8} sx={t9}>{t19}{t24}{t25}{t26}</Stack></Box>;
    $[52] = t19;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t27 || $[58] !== t5 || $[59] !== t6) {
    t28 = <PanelWrapper header={t5} footer={t6}>{t27}</PanelWrapper>;
    $[57] = t27;
    $[58] = t5;
    $[59] = t6;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== open || $[62] !== t28) {
    t29 = <BottomDialog open={open} isFullScreen={true}>{t28}</BottomDialog>;
    $[61] = open;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
};
const BucketSelector = t0 => {
  const $ = _c(11);
  const {
    disabled,
    bucketName,
    setBucketName,
    account,
    region
  } = t0;
  const {
    buckets,
    isLoading
  } = useBuckets(account, region);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Bucket</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = disabled || isLoading;
  const t3 = bucketName || "";
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== setBucketName) {
    t5 = event => {
      setBucketName(event.target.value);
    };
    $[2] = setBucketName;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== buckets) {
    t6 = buckets.map(_temp2);
    $[4] = buckets;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t5 || $[9] !== t6) {
    t7 = <FormControl size="small" className="w-full px-[0px]">{t1}<Select disabled={t2} size="small" value={t3} inputProps={t4} displayEmpty={true} renderValue={_temp} onChange={t5}>{t6}</Select></FormControl>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
const VaultAccountSelector = t0 => {
  const $ = _c(15);
  const {
    disabled,
    vaultAccountId,
    setAccountId,
    vaultAccounts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FormLabel>Vault account</FormLabel>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = vaultAccountId || "";
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      "data-testid": "vault-bucket-name"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== vaultAccounts) {
    t4 = value => {
      if (!value) {
        return <SelectPlaceholder />;
      }
      return vaultAccounts.find(account => account.id === value)?.name || "";
    };
    $[2] = vaultAccounts;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== setAccountId || $[5] !== vaultAccountId) {
    t5 = event => {
      if (event.target.value === vaultAccountId || !event.target.value) {
        return;
      }
      setAccountId(event.target.value);
    };
    $[4] = setAccountId;
    $[5] = vaultAccountId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== vaultAccounts) {
    t6 = vaultAccounts.map(_temp3);
    $[7] = vaultAccounts;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== disabled || $[10] !== t2 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <FormControl size="small" className="w-full px-[0px]">{t1}<Select disabled={disabled} size="small" value={t2} inputProps={t3} displayEmpty={true} renderValue={t4} onChange={t5}>{t6}</Select></FormControl>;
    $[9] = disabled;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
function _temp(value) {
  if (!value) {
    return <SelectPlaceholder />;
  }
  return value;
}
function _temp2(bucket) {
  return <MenuItem key={bucket.bucketName} value={bucket.bucketName}>{bucket.bucketName}</MenuItem>;
}
function _temp3(account_0) {
  return <Tooltip key={account_0.id} title={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED ? "Disconnected" : ""} placement="right"><span><MenuItem value={account_0.id} disabled={account_0.state === AccountState.ACCOUNT_STATE_DISCONNECTED}>{account_0.name}</MenuItem></span></Tooltip>;
}